import { useEffect, useMemo, useRef, useState } from 'react';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText, accountLocale } from '../../i18n';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { endpoints } from '../../api';
import { StudioSheet } from '../studio/StudioSheet';
import { StudioSettings } from '../studio/StudioFormParts';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioArtwork } from '../studio/use-studio-artwork';
import type { StudioApp } from '../studio/studio-api';
import { studioArtPhotos } from '../studio/studio-art-model';
import { stagePhotoResult } from '../photo/photo-result-entry';
import { CreateFoot, RightsBox, ScreenHead, TextSlot } from '../shoot/kit/FormParts';
import { limitOf, fieldOf, plural } from '../shoot/kit/form-model';
import { OrderTail } from '../shoot/kit/OrderTail';
import { collageLayouts, type CollageLayout } from './collage-renderer.js';
import { CollageCanvas } from './CollageCanvas';
import './collage.css';
import './native-collage.css';

const ratios = { '1:1': 'Квадрат', '4:5': 'Пост', '3:4': 'Портрет', '2:3': 'Печать 10 × 15', '9:16': 'Сторис', '16:9': 'Широкий' };
const backgrounds = { white: 'Белый', black: 'Чёрный', paper: 'Бумага', photo: 'Цвет из фото', blur: 'Размытие из фото' };
const gaps = { none: 'Нет', thin: 'Тонкие', medium: 'Средние', wide: 'Широкие' };
/** В ячейке карточки — коротко (APP-SPEC-5 §15 п. 4): «Из фото», «Размытие» */
const backgroundSlot: Record<string, string> = { photo: 'Из фото', blur: 'Размытие' };
const backgroundDot: Record<string, string> = { white: '#fff', black: '#111', paper: '#efe7d6' };
function shuffledIndices(length: number) {
  const indices = Array.from({ length }, (_, index) => index);
  const random = crypto.getRandomValues(new Uint32Array(length));
  for (let i = length - 1; i > 0; i--) {
    const j = random[i] % (i + 1);
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

export function CollageOrderForm({
  app,
  avatar,
  initialInputs,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
}) {
  const initial = useMemo(
    () => ({ layout: 'mosaic', ratio: '1:1', gap: 'medium', background: 'white', caption: '', ...initialInputs }),
    [initialInputs],
  );
  const state = useStudioForm({ app, avatar, initialInputs: initial });
  const { form, inputs, locked } = state;
  const captionPresets = [
    uiFormat('Лето {0}', new Date().getFullYear()),
    'Наши выходные',
    'С днём рождения',
    new Date().toLocaleDateString(accountLocale(), { day: 'numeric', month: 'long', year: 'numeric' }),
  ];
  const locale = accountLocale() === 'ru' ? 'ru' : 'en';
  const art = useStudioArtwork(locale);
  const slots =
    form?.fields
      .filter(field => /^photo_\d+$/.test(field.key) && field.upload?.kind === 'photo')
      .sort((a, b) => a.key.localeCompare(b.key)) ?? [];
  const previews = useStudioPhotoPreviews(state, slots);
  const input = useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = useState<'layouts' | null>(null);
  const [peek, setPeek] = useState<CollageLayout | null>(null);
  const peekFromGrid = useRef(false);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);
  const cancelHold = () => {
    clearTimeout(hold.current);
    hold.current = undefined;
  };
  useEffect(() => () => clearTimeout(hold.current), []);
  const inspect = (layout: CollageLayout) => {
    peekFromGrid.current = sheet === 'layouts';
    setSheet(null);
    setPeek(layout);
  };
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');
  const [theme, setTheme] = useState('all');
  const selectedSlots = slots.filter(slot => inputs[slot.key] || state.uploadIds[slot.key]);
  const photos = selectedSlots.map(slot => (previews.urls[slot.key] ? { url: previews.urls[slot.key], pos: 40 } : null));
  const examples = studioArtPhotos(art.data, undefined)
    .slice(0, 9)
    .map(url => ({ url, pos: 40 }));
  /* Без своих фото у каждой раскладки столько ячеек, сколько в её примере (L.ex, kOf и photosFor шаблона):
     девять примеров каталога иначе дают L.max ячеек. Примеров меньше — повторяем по кругу, геометрия та же. */
  const photosFor = (layout: CollageLayout) =>
    photos.length ? photos : examples.length ? Array.from({ length: layout.ex }, (_, index) => examples[index % examples.length]) : [];
  const available = collageLayouts.filter(layout => form?.fields.find(field => field.key === 'layout')?.values.includes(layout.value));
  const current = available.find(layout => layout.value === inputs.layout) ?? available[0];
  const minimum = Math.max(form?.constraints?.min_photos ?? 2, current?.min ?? 2);
  const ready = slots.filter(slot => !!inputs[slot.key]).length;
  const captionField = fieldOf(form, 'caption');
  const captionLimit = limitOf(captionField, 40);
  const missing = !ready
    ? uiFormat('Загрузите от {0} до {1} фото', form?.constraints?.min_photos ?? 2, slots.length || 9)
    : !state.rights
      ? 'Подтвердите права на материалы'
      : ready < minimum
        ? uiFormat('Для раскладки «{0}» нужно ещё {1}', current?.n ?? '', plural(minimum - ready, 'фото', 'фото', 'фото'))
        : Array.from(inputs.caption ?? '').length > captionLimit
          ? uiFormat('Подпись длиннее {0} знаков', captionLimit)
          : null;
  const titled = current?.id === 'mag' || current?.id === 'mood';
  const set = (key: string, value: string) => state.setInputs(previous => ({ ...previous, [key]: value }));
  const canvasProps = {
    ratio: inputs.ratio || '1:1',
    gap: inputs.gap || 'medium',
    background: inputs.background || 'white',
    caption: inputs.caption || '',
  };
  const choose = (layout: CollageLayout) => {
    set('layout', layout.value);
    setPeek(null);
    setSheet(null);
  };
  const swap = (from: number, to: number) => {
    if (locked || from === to || !selectedSlots[from] || !selectedSlots[to]) return;
    const a = selectedSlots[from].key,
      b = selectedSlots[to].key;
    state.setInputs(previous => ({ ...previous, [a]: previous[b] ?? '', [b]: previous[a] ?? '' }));
    state.setUploadIds(previous => ({ ...previous, [a]: previous[b] ?? '', [b]: previous[a] ?? '' }));
  };
  const railCard = (layout: CollageLayout) => (
    <div className="studio-layout-item" key={layout.value}>
      <button
        type="button"
        className={`bcs-c${layout.value === inputs.layout ? ' on' : ''}`}
        disabled={locked}
        aria-pressed={layout.value === inputs.layout}
        onPointerDown={() => {
          held.current = false;
          cancelHold();
          hold.current = setTimeout(() => {
            held.current = true;
            inspect(layout);
          }, 400);
        }}
        onPointerUp={cancelHold}
        onPointerCancel={cancelHold}
        onPointerLeave={cancelHold}
        onClick={event => {
          if (event.detail === 0 || !held.current) choose(layout);
        }}
        onContextMenu={event => {
          event.preventDefault();
          inspect(layout);
        }}
      >
        <span className="bcs-pic">
          <CollageCanvas {...canvasProps} photos={photosFor(layout)} layout={layout.value} />
          {layout.b && <span className="bcs-badge">{uiText(layout.b)}</span>}
        </span>
        <b>{uiText(layout.n)}</b>
        <small>{uiText(layout.d)}</small>
      </button>
      <button
        type="button"
        className="studio-layout-peek"
        aria-label={uiFormat('Рассмотреть раскладку {0}', layout.n)}
        onClick={event => {
          event.stopPropagation();
          inspect(layout);
        }}
      >
        ↗
      </button>
    </div>
  );
  return (
    <section className="native-studio-form native-collage cl-in">
      {/* На странице заказа заголовок экрана не повторяется: название уже в шапке (кадры *-done) */}
      {!state.orderId && <ScreenHead title={app.name} unit={form?.chargeable ? uiFormat('{0} C за коллаж', form.expected_price) : null} />}
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <p role="alert">
          {uiText(state.error)}{' '}
          <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (!state.busy && !state.submissionBlocked && (state.intent || (!missing && form.available))) void state.submit();
          }}
        >
          <StudioPhotoTray
            state={state}
            slots={slots}
            urls={previews.urls}
            minimum={form.constraints?.min_photos ?? 2}
            inputRef={input}
            description="Порядок поменяете прямо в раскладке"
            consent={<RightsBox checked={state.rights} disabled={locked} text="Подтверждаю право использовать загружаемые материалы" onChange={state.setRights} />}
          />
          {current && (
            <div className="cl-pvw">
              <div className="cl-pv">
                <div className="pc">
                  <CollageCanvas
                    {...canvasProps}
                    photos={photosFor(current)}
                    layout={current.value}
                    onSlot={index => {
                      if (!photos[index] && !locked) input.current?.click();
                    }}
                    onSwap={selectedSlots.length && !locked ? swap : undefined}
                  />
                  {!photos.length && <span className="cl-ex">{uiText('Пример')}</span>}
                </div>
              </div>
              <div className="cl-pvf">
                <span className="cl-hint">
                  {uiText(
                    !photos.length
                      ? 'Загрузите свои фото: раскладка соберётся на них'
                      : ready < minimum
                        ? uiFormat('Добавьте фото: нужно не меньше {0}', minimum)
                        : selectedSlots.length > current.max
                          ? uiFormat('В раскладку войдёт {0} фото из {1}', current.max, selectedSlots.length)
                          : 'Удерживайте фото и ведите на другое, чтобы поменять их местами',
                  )}
                </span>
                <button
                  type="button"
                  className="cl-shuf"
                  disabled={locked || ready < 2}
                  onClick={() => {
                    const indices = shuffledIndices(selectedSlots.length);
                    const order = selectedSlots.map((_, index) => selectedSlots[indices[index]].key);
                    state.setInputs(previous => ({
                      ...previous,
                      ...Object.fromEntries(selectedSlots.map((slot, index) => [slot.key, previous[order[index]] ?? ''])),
                    }));
                    state.setUploadIds(previous => ({
                      ...previous,
                      ...Object.fromEntries(selectedSlots.map((slot, index) => [slot.key, previous[order[index]] ?? ''])),
                    }));
                  }}
                >
                  {uiText('Перемешать')}
                </button>
              </div>
            </div>
          )}
          {previews.error && (
            <p role="alert">
              {uiText('Не удалось загрузить превью.')}{' '}
              <button type="button" onClick={previews.retry}>
                {uiText('Повторить')}
              </button>
            </p>
          )}
          <section className="bcs">
            <div className="bcs-h">
              <b>{uiText('Раскладка')}</b>
              <button type="button" onClick={() => setSheet('layouts')}>
                {uiFormat('Все {0}', available.length)} ›
              </button>
            </div>
            <div className="studio-layout-rail">{available.map(railCard)}</div>
          </section>
          <StudioSettings
            disabled={locked}
            slots={[
              { title: 'Формат', value: ratios[inputs.ratio as keyof typeof ratios] || 'Квадрат' },
              { title: 'Фон', value: backgroundSlot[inputs.background] || backgrounds[inputs.background as keyof typeof backgrounds] || 'Белый' },
              { title: 'Отступы', value: gaps[inputs.gap as keyof typeof gaps] || 'Средние' },
              ...(captionField ? [{ title: titled ? 'Заголовок' : 'Подпись', value: inputs.caption || 'Добавить' }] : []),
            ]}
          >
            {(['ratio', 'background', 'gap'] as const).map(key => {
              const labels = key === 'ratio' ? ratios : key === 'background' ? backgrounds : gaps;
              const field = form.fields.find(field => field.key === key);
              if (!field) return null;
              return (
                <ChoiceTiles
                  key={key}
                  label={uiText(key === 'ratio' ? 'Формат' : key === 'background' ? 'Фон' : 'Отступы')}
                  variant={key === 'gap' ? 'segments' : 'rows'}
                  value={inputs[key] || ''}
                  disabled={locked}
                  choices={field.values
                    .filter(value => value in labels)
                    .map(value => ({
                      value,
                      label: uiText((labels as Record<string, string>)[value]),
                      description: key === 'ratio' ? value.replace(':', ' : ') : undefined,
                      icon:
                        key === 'background' ? (
                          <span
                            className={`cl-dot${backgroundDot[value] ? '' : ` ${value}`}`}
                            style={backgroundDot[value] ? { background: backgroundDot[value] } : undefined}
                          />
                        ) : undefined,
                    }))}
                  onChange={value => set(key, value)}
                />
              );
            })}
            {captionField && (
              <TextSlot
                label={titled ? 'Заголовок' : 'Подпись'}
                value={inputs.caption || ''}
                limit={captionLimit}
                disabled={locked}
                chips={captionPresets}
                mode="replace"
                improve={false}
                note="Наберём нашим шрифтом"
                onChange={value => set('caption', value)}
              />
            )}
          </StudioSettings>
          <CreateFoot
            label={uiFormat('Создать коллаж · {0} C', form.expected_price)}
            busy={state.busy}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            missing={missing}
            available={form.available}
            onCreate={() => void state.submit()}
          />
        </form>
      )}
      {sheet === 'layouts' && (
        <StudioSheet title="Все раскладки" onClose={() => setSheet(null)}>
          <h2>{uiText('Все раскладки')}</h2>
          <input
            type="search"
            aria-label={uiText('Найти раскладку')}
            placeholder={uiText('Найти раскладку')}
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
          <ChoiceTiles
            label={uiText('Раскладки')}
            choices={[
              { value: 'all', label: uiText('Все') },
              { value: 'fit', label: uiText('Подходят') },
              { value: 'new', label: uiText('Новые') },
            ]}
            value={tab}
            onChange={setTab}
          />
          <ChoiceTiles
            label={uiText('Темы')}
            choices={['all', 'Сетки', 'Свободно', 'Ретро', 'С текстом', 'Для соцсетей'].map(value => ({
              value,
              label: uiText(value === 'all' ? 'Все' : value),
            }))}
            value={theme}
            onChange={setTheme}
          />
          <div className="studio-layout-grid">
            {available
              .filter(
                layout =>
                  (!query.trim() || `${layout.n} ${layout.d}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())) &&
                  (tab !== 'fit' || (ready >= layout.min && ready <= layout.max)) &&
                  (tab !== 'new' || layout.isNew) &&
                  (theme === 'all' || layout.th.includes(theme)),
              )
              .map(railCard)}
          </div>
        </StudioSheet>
      )}
      {peek && (
        <StudioSheet
          title={peek.n}
          onClose={() => {
            setPeek(null);
            if (peekFromGrid.current) setSheet('layouts');
          }}
        >
          <h2>{uiText(peek.n)}</h2>
          <CollageCanvas {...canvasProps} photos={photosFor(peek)} layout={peek.value} />
          <p>{uiText(peek.d)}</p>
          <button type="button" className="btn" disabled={locked} onClick={() => choose(peek)}>
            {uiText('Выбрать')}
          </button>
        </StudioSheet>
      )}
      <OrderTail
        state={state}
        kind="collage"
        groupLabel={current?.n ?? 'Коллаж'}
        copy={{
          running: ['Собираем коллаж', 'Можно уйти с экрана: бот пришлёт коллаж в чат.'],
          done: ['Коллаж готов', current?.id === 'pano' ? 'Слайды по порядку уже в чате, в отдельной теме.' : 'Бот уже прислал его в чат, в отдельную тему.'],
          failed: ['Коллаж не создан', 'Измените подпись или замените фото и попробуйте ещё раз.'],
        }}
        card={[
          current?.n,
          // Число фото — из самого заказа: после перезагрузки страницы лоток формы пуст, а заказ тот же
          plural(slots.filter(slot => !!(state.intent?.body.inputs as Record<string, unknown> | undefined)?.[slot.key]).length || ready, 'фото', 'фото', 'фото'),
          (ratios[inputs.ratio as keyof typeof ratios] || '').toLocaleLowerCase(),
          inputs.background ? `${uiText('фон')} ${(backgrounds[inputs.background as keyof typeof backgrounds] || '').toLocaleLowerCase()}` : '',
          inputs.gap ? `${(gaps[inputs.gap as keyof typeof gaps] || '').toLocaleLowerCase()} ${uiText('отступы')}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        saveLabel="Скачать"
        again="Ещё раскладка"
        onAgain={() => {
          // следующая раскладка, которая берёт все ваши фото (APP-SPEC-5 §15 «Хвост»)
          const index = available.findIndex(layout => layout.value === current?.value);
          const next = [...available.slice(index + 1), ...available.slice(0, index + 1)].find(layout => ready >= layout.min && ready <= layout.max && layout.value !== current?.value);
          if (next) set('layout', next.value);
        }}
        failLabel={inputs.caption ? 'Изменить подпись' : 'Заменить фото'}
        next={[
          { label: 'Изменить', icon: 'edit', onClick: () => void state.returnToForm() },
          {
            label: 'Рилс из этих фото',
            icon: 'film',
            onClick: images => {
              const image = images[0];
              if (!image || !state.order) return;
              void endpoints.me().then(me => {
                if (me.user.id !== state.intent?.owner || !state.order) return;
                stagePhotoResult({
                  owner: me.user.id,
                  orderId: state.order.id,
                  revision: state.order.result_revision,
                  index: image.index,
                  file: new File([image.blob], image.name, { type: image.mime }),
                  destination: 'reels',
                });
              });
            },
          },
        ]}
      />
    </section>
  );
}
